import { isValidFile } from '../shared/utils/file-upload-utils';
import { UPLOAD_ERROR_MESSAGES } from '../shared/constants';

// File-upload handlers. The upload STATE (imageData / isLoading / documentFile /
// isDocumentLoading) lives in the composer store slice; this hook writes to it
// directly through the passed `store`, so there is no provider effect-bridge
// mirroring hook `useState` into the store. Consumers read the state via
// useAssistantStore selectors; this hook only returns the handlers.
//
// @param {(msg: string|null) => void} setError - upload-error setter (composer slice)
// @param {{ getState: () => object }} store - the assistant store (composer slice actions)
export const useFileUpload = (setError, store) => {
  const {
    setImageData, setIsLoading, setDocumentFile, setIsDocumentLoading,
  } = store.getState();

  // Function to handle file input and convert image to base64
  const handleSetFile = (file, imageOrigin) => {
    try {
      if (!file) throw new Error('No file provided');
      setIsLoading(true);
      if (isValidFile(file, setError)) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setError(null);

          const imageFileType = file.name.split('.').pop().toLowerCase();

          const img = new window.Image();
          img.onload = () => {
            setImageData({
              base64: reader.result,
              file,
              fileSize: file.size,
              aspectRatio: img.naturalWidth / img.naturalHeight,
              imageOrigin,
              imageFileType,
            });
            setIsLoading(false);
          };
          img.onerror = () => {
            setImageData({
              base64: reader.result,
              file,
              fileSize: file.size,
              aspectRatio: null,
              imageOrigin,
              imageFileType,
            });
            setIsLoading(false);
          };
          img.src = reader.result;
        };
        // Read the file as a data URL (base64)
        reader.readAsDataURL(file);
      } else {
        setImageData(null);
        setIsLoading(false);
      }
    } catch (error) {
      setError(UPLOAD_ERROR_MESSAGES.GENERIC_ERROR);
      setIsLoading(false);
      throw error;
    }
  };

  const clearImage = () => {
    setImageData(null);
    setError(null);
  };

  const clearDocument = () => {
    setDocumentFile(null);
  };

  const handleDocumentChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setIsDocumentLoading(true);
    setDocumentFile(null);
    const reader = new FileReader();
    reader.onloadend = () => {
      setDocumentFile({ name: file.name });
      setIsDocumentLoading(false);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleImageChange = (event, imageOrigin) => {
    const file = event.target.files[0];
    handleSetFile(file, imageOrigin);
  };

  return {
    handleImageChange,
    handleSetFile,
    clearImage,
    handleDocumentChange,
    clearDocument,
  };
};
